/**
 * EmbeddedAssetBlock Component
 *
 * Bridge component for rendering embedded Contentful assets within Rich Text.
 * This component is used by jsxParser to convert placeholder divs into actual components.
 *
 * The placeholder HTML: <div role="embedded-asset-block" data-id="..." data-asset="..."></div>
 * is converted by jsxParser using RAW_COMPONENTS mapping.
 */

import PropTypes from 'prop-types';

import {Box, Image, Skeleton} from '@salesforce/retail-react-app/app/components/shared/ui';

/**
 * Parse asset data from data-asset attribute
 *
 * @param {string} dataAsset - JSON string of asset data
 * @returns {Object|null}
 */
const parseAssetData = (dataAsset) => {
  if (!dataAsset) return null;

  try {
    return JSON.parse(dataAsset);
  } catch (error) {
    console.error('[EmbeddedAssetBlock] Failed to parse asset data:', error);

    return null;
  }
};

/**
 * EmbeddedAssetBlock Component
 *
 * Renders an embedded Contentful asset (image/video).
 *
 * @param {Object} props - Component props
 * @param {string} props.data-id - Asset ID
 * @param {string} props.data-asset - JSON string of asset data
 */
export const EmbeddedAssetBlock = ({'data-id': dataId, 'data-asset': dataAsset, ...props}) => {
  const asset = parseAssetData(dataAsset);

  // If no valid asset data, show placeholder
  // This can happen when links query fails or asset is not published
  if (!asset || !asset.url) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`[EmbeddedAssetBlock] Missing data for asset: ${dataId}. Links may not have loaded.`);
    }

    return (
      <Box className="embedded-asset-block embedded-asset-block--missing" my={6} {...props}>
        <Skeleton height="200px" width="100%" />
      </Box>
    );
  }

  const isVideo = asset.contentType?.startsWith('video/');

  return (
    <Box className="embedded-asset-block" my={6} {...props}>
      {isVideo ? (
        <Box
          as="video"
          controls
          playsInline
          src={asset.url}
          width="100%"
        >
          Your browser does not support the video tag.
        </Box>
      ) : (
        <Image
          alt={asset.title || asset.description || ''}
          src={asset.url}
          width="100%"
        />
      )}
    </Box>
  );
};

EmbeddedAssetBlock.displayName = 'EmbeddedAssetBlock';
EmbeddedAssetBlock.propTypes = {
  'data-id': PropTypes.string,
  'data-asset': PropTypes.string,
};

export default EmbeddedAssetBlock;
